// =============================================================
// 📁 src/pages/planificateur/Envoyees.tsx
// 💎 Version Platinum PRO 2025 — Descentes envoyées
// =============================================================

import React, { useState, useEffect, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { api } from "../../../lib/axios";
import Badge from "../../../components/ui/Badge";

/* =============================================================
   ⏳ Loader minimaliste
============================================================= */
const Loader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-900 text-gray-400">
    ⏳ Chargement des descentes envoyées...
  </div>
);
 
/* =============================================================
   📦 Typage
============================================================= */
interface Descente {
  id: number;
  numero?: string; // ✅ nouveau champ pour numéro
  titre: string;
  date: string;
  zone: string;
  client?: string;
  camion?: string;
  chauffeur?: string;
  equipe: string;
  statut: "PLANIFIEE" | "EN_COURS" | "CLOTUREE" | "ANNULEE";
  alerte?: string;
  lat?: number;
  lng?: number;
  archivée?: boolean;
}

/* =============================================================
   🧩 Composant principal
============================================================= */
const Envoyees: React.FC = () => {
  const navigate = useNavigate();
  const [descentes, setDescentes] = useState<Descente[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

/* =============================================================
   🚀 Chargement initial — Version Platinum PRO (Corrigée)
============================================================= */
useEffect(() => {
  const fetchDescentes = async () => {
    try {
      const res = await api.get("/descentes");
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];

      const mapped = raw.map((d: any) => {
        return {
          id: d.id,
          numero: d.numero || `DSC-${d.id}`, // Utilise le numéro du backend
          titre: d.titre || "—",
          date: d.date,
          
          // 🔥 On utilise directement les chaînes formatées par le backend
          zone: d.zone || "—",           
          client: d.clientLabel || "—",  
          camion: d.vehicule || "—",     // Le backend envoie déjà l'immatriculation
          chauffeur: d.chauffeur || "—", // Le backend envoie déjà le nom complet
          equipe: d.equipe || "—",       
          
          statut: d.statut,
          alerte: d.alerte ?? undefined,
          lat: d.latitude ?? undefined,
          lng: d.longitude ?? undefined,
          archivée: d.archivée ?? false,
        };
      });
 
      setDescentes(mapped);

    } catch (error) {
      console.error("Erreur chargement :", error);
      toast.error("❌ Erreur lors du chargement des descentes");
    } finally {
      setLoading(false);
    }
  };

  fetchDescentes();
}, []);


useEffect(() => {
  setPage(1);
}, [search, filtreStatut, dateFrom, dateTo]);



const stats = useMemo(() => {
  return {
    planifiees: descentes.filter(d => d.statut === "PLANIFIEE").length,
    enCours: descentes.filter(d => d.statut === "EN_COURS").length,
    cloturees: descentes.filter(d => d.statut === "CLOTUREE").length,
  };
}, [descentes]);

/* =============================================================
   🔍 Filtres + Recherche étendue (client inclus)
============================================================= */
const filtered = useMemo(() => {
  const searchTerm = search.trim().toLowerCase();

  return descentes.filter((d) => {
    /* =========================
       🔹 Filtre Statut
    ========================= */
    const matchStatut = filtreStatut
      ? d.statut === filtreStatut
      : true;

    /* =========================
       🔹 Filtre Date sécurisé
    ========================= */
    if (!d.date) return false;

    const dateObj = new Date(d.date);
    if (isNaN(dateObj.getTime())) return false;

    dateObj.setHours(0, 0, 0, 0);

    const matchDateFrom = dateFrom
      ? dateObj >= new Date(`${dateFrom}T00:00:00`)
      : true;

    const matchDateTo = dateTo
      ? dateObj <= new Date(`${dateTo}T23:59:59`)
      : true;

    /* =========================
       🔹 Recherche texte
    ========================= */
    if (!searchTerm) {
      return matchStatut && matchDateFrom && matchDateTo;
    }

    const fields = [
      d.titre,
      d.zone,
      d.client,
      d.equipe,
      d.camion,
      d.chauffeur,
    ];
 
    const matchSearch = fields
      .filter((val): val is string => typeof val === "string")
      .some((val) =>
        val.toLowerCase().includes(searchTerm)
      );

    return matchSearch && matchStatut && matchDateFrom && matchDateTo;
  });
}, [descentes, search, filtreStatut, dateFrom, dateTo]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
const paginated = useMemo(() => {
  return filtered.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
}, [filtered, page]);

useEffect(() => {
  if (page > totalPages && totalPages > 0) {
    setPage(totalPages);
  }
}, [page, totalPages]);

const setToday = () => {
  const today = new Date().toISOString().split("T")[0];
  setDateFrom(today);
  setDateTo(today);
};

const setYesterday = () => {
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const formatted = y.toISOString().split("T")[0];
  setDateFrom(formatted);
  setDateTo(formatted);
};

const getStatutLabel = (statut: Descente["statut"]) => {
  switch (statut) {
    case "PLANIFIEE":
      return "Planifiée";
    case "EN_COURS":
      return "Envoyée";
    case "CLOTUREE":
      return "Validée";
    case "ANNULEE":
      return "Annulée";
    default:
      return statut;
  }
};

const getStatutVariant = (statut: Descente["statut"]) => {
  switch (statut) {
    case "CLOTUREE":
      return "success";
    case "ANNULEE":
      return "error";
    case "EN_COURS":
      return "warning";
    default:
      return "default";
  }
};

const resetDates = () => {
  setDateFrom("");
  setDateTo("");
};

const handleStartMission = async (id: number) => {
  try {
    const res = await api.put(`/descentes/${id}/start`);

    // si ton backend renvoie la descente mise à jour
    const updated = res.data?.data || res.data;

    setDescentes((prev) =>
      prev.map((d) =>
        d.id === id
          ? { ...d, statut: updated?.statut ?? "EN_COURS" }
          : d
      )
    );

    toast.success("🚀 Mission démarrée avec succès");
  } catch (error: any) {
    toast.error(
      error?.response?.data?.error ||
        "❌ Impossible de démarrer la mission"
    );
  }
};

/* =============================================================
   📄 Export PDF / Excel — Version alignée client
============================================================= */
const exportPDF = () => {
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text("Descentes envoyées", 14, 16);

  doc.setFontSize(10);
  doc.text(
    `Export généré le ${new Date().toLocaleDateString("fr-FR")}`,
    14,
    22
  );

  autoTable(doc, {
    startY: 28,
    head: [
      [
        "Titre",
        "Date",
        "Zone",
        "Client",
        "Camion",
        "Chauffeur",
        "Équipe",
        "Statut",
      ],
    ],
    body: filtered.map((d) => [
      d.titre || "—",
      d.date
        ? new Date(d.date).toLocaleDateString("fr-FR")
        : "—",
      d.zone || "—",
      d.client || "—",
      d.camion || "—",
      d.chauffeur || "—",
      d.equipe || "—",
      getStatutLabel(d.statut), // ✅ Label propre
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      fillColor: [30, 41, 59], // slate-800
      textColor: 255,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252], // léger gris
    },
  });

  doc.save("descentes_envoyees.pdf");
  toast.success("✅ Export PDF terminé");
};

const exportExcel = () => {
  const data = filtered.map((d) => ({
    Titre: d.titre || "—",
    Date: d.date
      ? new Date(d.date).toLocaleDateString("fr-FR")
      : "—",
    Zone: d.zone || "—",
    Client: d.client || "—",
    Camion: d.camion || "—",
    Chauffeur: d.chauffeur || "—",
    Équipe: d.equipe || "—",
    Statut: getStatutLabel(d.statut), // ✅ label propre
  }));

  const ws = XLSX.utils.json_to_sheet(data);

  // 🔹 Auto largeur colonnes
  const colWidths = Object.keys(data[0] || {}).map((key) => ({
    wch: Math.max(
      key.length,
      ...data.map((row) => String(row[key as keyof typeof row]).length)
    ) + 2,
  }));

  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Descentes");

  XLSX.writeFile(
    wb,
    `descentes_envoyees_${new Date()
      .toISOString()
      .split("T")[0]}.xlsx`
  );

  toast.success("✅ Export Excel terminé");
};
 
/* =============================================================
   ⚙️ Archivage mission — sécurisé métier
============================================================= */
const handleArchivage = async (id: number) => {
  const mission = descentes.find((d) => d.id === id);

  if (!mission) {
    return toast.error("❌ Mission introuvable.");
  }

  // 🔒 Règle métier : seulement les missions clôturées
  if (mission.statut !== "CLOTUREE") {
    return toast.warning(
      "⚠️ Seules les missions validées peuvent être archivées."
    );
  }

  try {
    const res = await api.put(`/descentes/${id}`, {
      archivée: true,
    });

    const updated = res.data?.data || res.data;

    setDescentes((prev) =>
      prev.map((d) =>
        d.id === id
          ? { ...d, archivée: updated?.archivée ?? true }
          : d
      )
    );

    toast.success("📁 Mission archivée avec succès");
  } catch (error: any) {
    toast.error(
      error?.response?.data?.error ||
        "❌ Échec de l'archivage"
    );
  }
};

  const handleDownloadFiche = (d: Descente) => {
    const doc = new jsPDF();
    doc.text(`📄 Fiche de descente : ${d.titre}`, 14, 16);
    autoTable(doc, {
      startY: 26,
      head: [["Champ", "Valeur"]],
      body: [
        ["Titre", d.titre],
        ["Date", new Date(d.date).toLocaleDateString("fr-FR")],
        ["Zone", d.zone],
        ["Camion", d.camion || "-"],
        ["Chauffeur", d.chauffeur || "-"],
        ["Équipe", d.equipe],
        ["Statut", d.statut],
      ],
    });
    doc.save(`descente-${d.id}.pdf`);
    toast.info(`🧾 Fiche mission #${d.id} générée`);
  };

  if (loading) return <Loader />;

  /* =============================================================
     🎨 Rendu principal Platinum PRO
  ============================================================= */
  return (
    <div className="min-h-screen bg-slate-900 text-gray-100 p-6">
      <ToastContainer theme="dark" />

      {/* ============================ */}
      {/* 🧭 Titre + Boutons d’action */}
      {/* ============================ */}
      <div className="flex flex-wrap justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          📤 Descentes envoyées
        </h1>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate(-1)}
            className="bg-slate-700 hover:bg-slate-600 text-gray-100 px-4 py-2 rounded-lg shadow-md transition"
          >
            ↩️ Retour
          </button>

          <button
            onClick={() => navigate("/dashboard")}
            className="bg-blue-700 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow-md transition"
          >
            🏠 Dashboard
          </button>

          <button
            onClick={exportPDF}
            className="bg-indigo-700 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-md transition"
          >
            📄 Export PDF
          </button>
          <button
            onClick={exportExcel}
            className="bg-green-700 hover:bg-green-600 text-white px-4 py-2 rounded-lg shadow-md transition"
          >
            📊 Export Excel
          </button>
        </div>
      </div>

{/* ============================ */}
{/* 🔍 Filtres Avancés */}
{/* ============================ */}
<div className="flex flex-wrap gap-3 mb-4 items-end">

  {/* Recherche texte */}
  <input
    type="text"
    placeholder="🔍 Rechercher (titre, zone, client, camion, équipe, chauffeur)"
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    className="bg-slate-800 border border-slate-700 px-4 py-2 rounded w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-100 placeholder-gray-400"
  />

  {/* Filtre Statut */}
  <select
    value={filtreStatut}
    onChange={(e) => setFiltreStatut(e.target.value as Descente["statut"] | "")}
    className="bg-slate-800 border border-slate-700 px-4 py-2 rounded text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <option value="">📁 Tous les statuts</option>
    <option value="PLANIFIEE">📅 Planifiée</option>
    <option value="EN_COURS">📤 Envoyée</option>
    <option value="CLOTUREE">✅ Validée</option>
    <option value="ANNULEE">❌ Annulée</option>
  </select>

  {/* Date Du */}
  <div className="flex flex-col">
    <label className="text-xs text-gray-400 mb-1">Du</label>
    <input
      type="date"
      value={dateFrom}
      onChange={(e) => setDateFrom(e.target.value)}
      className="bg-slate-800 border border-slate-700 px-3 py-2 rounded text-gray-100 focus:ring-2 focus:ring-blue-500"
    />
  </div>

  {/* Date Au */}
  <div className="flex flex-col">
    <label className="text-xs text-gray-400 mb-1">Au</label>
    <input
      type="date"
      value={dateTo}
      onChange={(e) => setDateTo(e.target.value)}
      className="bg-slate-800 border border-slate-700 px-3 py-2 rounded text-gray-100 focus:ring-2 focus:ring-blue-500"
    />
  </div>
 
  {/* Raccourcis */}
  <div className="flex gap-2">
    <button
      onClick={setToday}
      className="bg-indigo-700 hover:bg-indigo-600 px-3 py-2 rounded text-white text-sm"
    >
      📅 Aujourd’hui
    </button>

    <button
      onClick={setYesterday}
      className="bg-purple-700 hover:bg-purple-600 px-3 py-2 rounded text-white text-sm"
    >
      ⏮ Hier
    </button>

    <button
      onClick={resetDates}
      className="bg-slate-700 hover:bg-slate-600 px-3 py-2 rounded text-white text-sm"
    >
      🔄 Reset
    </button>
  </div>

</div>

{/* ============================ */}
{/* 📋 Tableau principal */}
{/* ============================ */}
<div className="overflow-auto rounded-lg shadow-lg border border-slate-700 bg-slate-900">
  <table className="min-w-full text-sm">
    <thead className="bg-slate-800 text-gray-300 uppercase text-xs font-semibold">
      <tr>
        <th className="p-3 text-left"># Descente</th> {/* Nouvelle colonne */}
        <th className="p-3 text-left">📋 Titre</th>
        <th className="p-3 text-left">📅 Date</th>
        <th className="p-3 text-left">📍 Zone</th>
        <th className="p-3 text-left">🏢 Client</th>
        <th className="p-3 text-left">🚛 Camion</th>
        <th className="p-3 text-left">👨‍🔧 Chauffeur</th>
        <th className="p-3 text-left">👥 Équipe</th>
        <th className="p-3 text-left">📌 Statut</th>
        <th className="p-3 text-left">⚠️ Alerte</th>
        <th className="p-3 text-left">🗺️ Coordonnées</th>
        <th className="p-3 text-right">⚙️ Actions</th>
      </tr>
    </thead>

    <tbody>
      {paginated.length === 0 ? (
        <tr>
          <td colSpan={11} className="p-6 text-center text-gray-500 italic">
            Aucune descente envoyée trouvée.
          </td>
        </tr>
      ) : (
        paginated.map((d) => {

          const formattedDate = d.date
            ? new Date(d.date).toLocaleDateString("fr-FR")
            : "—";

          const hasCoords =
            d.lat !== undefined &&
            d.lng !== undefined &&
            d.lat !== null &&
            d.lng !== null;

          // 🔴 Gestion RETARD (frontend uniquement)
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const missionDate = new Date(d.date);
          missionDate.setHours(0, 0, 0, 0);

          const isLate =
            d.statut === "PLANIFIEE" && missionDate < today;

          return (
            <tr
              key={d.id}
              className="border-t border-slate-700 hover:bg-slate-800 transition"
            >
 {/* Numéro de descente */}
          <td className="p-3 font-semibold text-blue-400">
            {d.numero ?? d.id} {/* si le backend renvoie numero */}
          </td>

              <td className="p-3 font-medium">{d.titre}</td>

              <td className="p-3">{formattedDate}</td>

              <td className="p-3">{d.zone || "—"}</td>

              <td className="p-3">{d.client || "—"}</td>

              <td className="p-3">{d.camion || "—"}</td>

              <td className="p-3">{d.chauffeur || "—"}</td>

              <td className="p-3">{d.equipe || "—"}</td>

              <td className="p-3">
                <div className="flex flex-col gap-1">
                  <Badge variant={getStatutVariant(d.statut)}>
                    {getStatutLabel(d.statut)}
                  </Badge>

                  {isLate && (
                    <span className="text-xs font-semibold text-red-500">
                      🔴 RETARD
                    </span>
                  )}
                </div>
              </td>

              <td className="p-3 text-yellow-400">
                {d.alerte || "—"}
              </td>

              <td className="p-3">
                {hasCoords ? (
                  <span className="text-green-400 font-medium">
                    ✅ OK
                  </span>
                ) : (
                  <span className="text-red-400">
                    ❌ Aucune
                  </span>
                )}
              </td>

              <td className="p-3 text-right flex justify-end gap-3">

                {/* 🚀 Démarrer mission */}
                {d.statut === "PLANIFIEE" && (
                  <button
                    onClick={() => handleStartMission(d.id)}
                    className="text-green-400 hover:text-green-300 transition"
                  >
                    🚀 Démarrer
                  </button>
                )}

                {/* 📄 Détails */}
                <button
                  onClick={() =>
                    navigate(`/admin/planificateur/fiche/${d.id}`)
                  }
                  className="text-blue-400 hover:text-blue-300 transition"
                >
                  Détails
                </button>

                {/* 📑 PDF */}
                <button
                  onClick={() => handleDownloadFiche(d)}
                  className="text-purple-400 hover:text-purple-300 transition"
                >
                  PDF
                </button>

                {/* 📁 Archivage */}
                <button
                  onClick={() => handleArchivage(d.id)}
                  disabled={d.archivée}
                  className={`${
                    d.archivée
                      ? "text-gray-600 cursor-not-allowed"
                      : "text-gray-400 hover:text-gray-300"
                  } transition`}
                >
                  📁 Archiver
                </button>

              </td>
            </tr>
          );
        })
      )}
    </tbody>
  </table>
</div>

{/* ============================ */}
{/* 📄 Pagination */}
{/* ============================ */}
{totalPages > 0 && (
  <div className="flex justify-between items-center text-sm text-gray-400 mt-4">

    <p>
      Page {page} sur {totalPages}
    </p>

    <div className="flex gap-2">

      <button
        onClick={() => setPage((p) => Math.max(p - 1, 1))}
        disabled={page === 1}
        className="bg-slate-800 border border-slate-700 px-3 py-1 rounded 
                   hover:bg-slate-700 transition
                   disabled:opacity-50 disabled:cursor-not-allowed"
      >
        ◀ Précédent
      </button>

      <button
        onClick={() =>
          setPage((p) => Math.min(p + 1, totalPages))
        }
        disabled={page === totalPages}
        className="bg-slate-800 border border-slate-700 px-3 py-1 rounded 
                   hover:bg-slate-700 transition
                   disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Suivant ▶
      </button>

    </div>
  </div>
)}

      {/* ============================ */}
      {/* ⚙️ Pied de page */}
      {/* ============================ */}
      <div className="text-center text-gray-500 text-xs py-4">
        © {new Date().getFullYear()} EspoirAfriqueSas — Tous droits réservés.
      </div>
    </div>
  );
};
 
export default Envoyees;